import { Calendar } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import type { BadgeColors } from '@/lib/base/badges/badge-types';
import { Badge } from '@/lib/base/badges/badges';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { useAssociations } from '../../hooks/useAssociations';
import { useOpenSlideout } from '../../hooks/useOpenSlideout';
import { CollapsibleSection } from './collapsible-section';
import { SectionErrorState, SectionLoadingState } from './section-states';

interface SchedulerBookingSectionProps {
  object: CrmObject;
}

const STATUS_COLOR_MAP: Record<
  string,
  'success' | 'blue' | 'error' | 'warning'
> = {
  confirmed: 'success',
  pending: 'blue',
  cancelled: 'error',
  rescheduled: 'warning',
};

export function SchedulerBookingSection({
  object,
}: SchedulerBookingSectionProps) {
  const orgId = object.org_id;

  const origin = useMemo(
    () => getPropertyValue<string>(object.properties?.ahoy_origin),
    [object.properties?.ahoy_origin]
  );

  const bookingAssociations = useMemo(
    () =>
      (object.associations ?? []).filter(
        assoc => assoc.association_type_name === 'ahoy_meeting_to_booking'
      ),
    [object.associations]
  );

  const associationsToFetch = useMemo(
    () => (origin === 'scheduler' ? bookingAssociations : []),
    [origin, bookingAssociations]
  );

  const { associations, isLoading, error, refetch } = useAssociations(
    orgId,
    associationsToFetch
  );

  const openSlideout = useOpenSlideout();

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  if (origin !== 'scheduler') {
    return null;
  }

  if (isLoading) {
    return <SectionLoadingState title='Scheduler booking' />;
  }

  if (error) {
    return (
      <SectionErrorState
        title='Scheduler booking'
        message="We couldn't load the booking"
        onRetry={handleRetry}
      />
    );
  }

  if (bookingAssociations.length === 0) {
    return (
      <CollapsibleSection title='Scheduler booking' defaultExpanded>
        <p className='text-xs md:text-sm text-[var(--color-text-tertiary)] py-2'>
          No linked booking found.
        </p>
        <p className='text-xs text-[var(--color-text-tertiary)]'>
          The booking association may not have been processed yet.
        </p>
      </CollapsibleSection>
    );
  }

  const firstAssoc = associations[0];
  const booking = firstAssoc?.object ?? null;

  if (!booking) {
    return (
      <CollapsibleSection title='Scheduler booking' defaultExpanded>
        <p className='text-xs md:text-sm text-[var(--color-text-tertiary)] py-2'>
          Linked booking not found.
        </p>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection title='Scheduler booking' defaultExpanded>
      <BookingCard booking={booking} onNavigate={openSlideout} />
    </CollapsibleSection>
  );
}

interface BookingCardProps {
  booking: CrmObject;
  onNavigate: (entityId: string) => void;
}

export function BookingCard({ booking, onNavigate }: BookingCardProps) {
  const status = getPropertyValue<string>(booking.properties?.ahoy_status);
  const cancelToken = getPropertyValue<string>(
    booking.properties?.ahoy_cancel_token
  );

  const statusColor: BadgeColors | undefined = status
    ? STATUS_COLOR_MAP[status]
    : undefined;

  const handleNavigate = useCallback(() => {
    onNavigate(booking.entity_id);
  }, [onNavigate, booking.entity_id]);

  const statusProp = useMemo(
    () =>
      status && statusColor
        ? {
            text: status.charAt(0).toUpperCase() + status.slice(1),
            color: statusColor,
          }
        : null,
    [status, statusColor]
  );

  return (
    <div className='rounded-xl border border-[var(--color-border-secondary)] px-4 py-3'>
      <div className='flex items-start justify-between gap-2'>
        <div className='flex items-start gap-2 min-w-0'>
          <Calendar className='h-4 w-4 mt-0.5 shrink-0 text-[var(--color-text-secondary)]' />
          <span className='text-xs md:text-sm text-[var(--color-text-secondary)]'>
            Booked through your scheduling link
          </span>
        </div>
        {statusProp && (
          <Badge
            type='pill-color'
            size='sm'
            color={statusProp.color}
            className='shrink-0'
          >
            {statusProp.text}
          </Badge>
        )}
      </div>
      <div className='flex items-center gap-3 mt-3'>
        {cancelToken && status !== 'cancelled' && (
          <a
            href={
              '/api/public/scheduling/bookings/reschedule/' + cancelToken + '/'
            }
            target='_blank'
            rel='noopener noreferrer'
            aria-label='Cancel or reschedule booking'
            className='text-xs text-fg-brand-primary hover:underline'
          >
            Cancel / Reschedule
          </a>
        )}
        <button
          type='button'
          onClick={handleNavigate}
          aria-label='View booking details'
          className='text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] underline hover:no-underline ml-auto'
        >
          View booking
        </button>
      </div>
    </div>
  );
}
